/**
 * port_client.ts — the port run's rows as the screens read them (space campaign E, 2026-09-30).
 *
 * One place that subscribes the slice's rows (the run terms, the receipts, the port's dry-chain status, every run
 * this pilot touched) and turns them into the inputs `lib/port_terms.ts` words. The dock's Haulage tab, the spaceport
 * prompt, the terms window and the in-flight relay card all read through here, so they cannot disagree.
 * Reads only (no reducer calls).
 */
import { Identity } from 'spacetimedb';
import { atlas } from '../state.js';
import { yardDefFor, portFirmIdFor, firmIdentityBits, applyPriceMod, priceMult, type ShareIn } from '@shared/space_economy.js';
import { marketEpoch, stationDrift, marketUnitPrice, sysTierOf } from '@shared/space_industry.js';
import { SYS0_STATIONS } from '@shared/space_stations.js';
import type { TermsInput, ReceiptView } from './port_terms.js';
import { CHASSIS } from './fleet.js';
import { settlementSites } from '@shared/settlements.js';
import { settlementNamesFor } from './settlement_names.js';
import { civilizationOf } from './biome_settlements.js';

export interface HaulRow { haulId: bigint; portId: bigint; direction: string; familyKey: string; qty: number; rewardSov: bigint; state: string; hauler: Identity; shipId: bigint; stationKey: string; acceptedAt: { microsSinceUnixEpoch: bigint }; deadlineAt: { microsSinceUnixEpoch: bigint } }
export interface TermsRow { haulId: bigint; portId: bigint; termsVersion: number; captain: Identity; splitJson: string; splitRev: number; relayPartner: Identity; custodianRev: number; relayFrom: Identity; offerTo: Identity; offerBps: number; offerExpiresAt: { microsSinceUnixEpoch: bigint }; beginBy: Identity; beginAt: { microsSinceUnixEpoch: bigint }; beginJson: string }
export interface ReceiptRow { haulId: bigint; portId: bigint; direction: string; civ: string; yardFirmId: bigint; stationKey: string; qty: number; captain: Identity; deliveredBy: Identity; custodianRev: number; termsVersion: number; splitRev: number; rewardSov: bigint; payoutJson: string; oreQuote: bigint; orePaid: bigint; yardOreAfter: number; yardHulls: number; yardTarget: number; shortHours: number; acceptedAt: { microsSinceUnixEpoch: bigint }; deliveredAt: { microsSinceUnixEpoch: bigint }; stage: string; wait: string; builtAt: { microsSinceUnixEpoch: bigint }; reserveAt: { microsSinceUnixEpoch: bigint }; reserveHulls: number; campaignId: bigint; committed: number }
export interface StatusRow { portId: bigint; procure: boolean; reasonsJson: string; numbersJson: string; checkedAt: { microsSinceUnixEpoch: bigint } }
export interface PortRow { portId: bigint; settlementId: bigint; firmId: bigint; state: string }

interface Db {
  portHaul?: { iter(): Iterable<HaulRow>; haulId?: { find(id: bigint): HaulRow | undefined } };
  portHaulTerms?: { iter(): Iterable<TermsRow>; haulId?: { find(id: bigint): TermsRow | undefined } };
  portReceipt?: { iter(): Iterable<ReceiptRow> };
  portStatus?: { iter(): Iterable<StatusRow> };
  planetaryPort?: { iter(): Iterable<PortRow> };
  account?: { iter(): Iterable<{ identity: Identity; username: string; displayName?: string }>; identity?: { find(id: Identity): { username: string; displayName?: string } | undefined } };
  wallet?: { identity?: { find(id: Identity): { sov: bigint } | undefined } };
  spaceFirm?: { iter(): Iterable<{ firmId: bigint; name: string; civ: string; kind: string; stationKey: string; shortHours: number; hulls: number; hullsTarget: number }> };
  spacePriceMod?: { iter(): Iterable<{ stationKey: string; commodity: string; mult: number }> };
  playerShip?: { iter(): Iterable<{ shipId: bigint; owner: Identity; name: string; active: boolean; chassisKey: string }> };
  cargoLot?: { iter(): Iterable<{ owner: Identity; locId: string; qty: number }> };
  spaceCrew?: { iter(): Iterable<{ captain: Identity; crew: Identity }> };
  partyMember?: { iter(): Iterable<{ partyId: bigint; identity: Identity }> };
}
const db = (): Db | null => (atlas.conn ? (atlas.conn.db as unknown as Db) : null);
const safe = <T>(f: () => T, fb: T): T => { try { return f(); } catch { return fb; } };

let subscribed = false;
/** Subscribe the slice's rows once per connection (a second handle beside the sector's is harmless). */
export function ensurePortRows(): void {
  const conn = atlas.conn;
  if (!conn || subscribed) return;
  subscribed = true;
  try {
    conn.subscriptionBuilder().subscribe([
      'SELECT * FROM planetary_port', 'SELECT * FROM port_status', 'SELECT * FROM port_haul_terms', 'SELECT * FROM port_receipt',
      'SELECT * FROM port_haul', 'SELECT * FROM space_firm', 'SELECT * FROM space_price_mod',
      // The custodian line needs MY hulls and hold even on the ground (only the sector subscribed them — the first
      // world picture said "no active hull" at the spaceport for a pilot who owns one).
      'SELECT * FROM player_ship WHERE owner = :sender', 'SELECT * FROM cargo_lot WHERE owner = :sender',
    ]);
  } catch { subscribed = false; /* pre-publish server */ }
}

export const ZERO = (): Identity => Identity.zero();
export const isZero = (x: Identity | undefined | null): boolean => !x || x.isEqual(Identity.zero());
export function me(): Identity | null { return atlas.conn?.identity ?? null; }
export function nameOf(id: Identity | null | undefined): string {
  if (!id || isZero(id)) return '—';
  const d = db();
  const a = safe(() => d?.account?.identity?.find(id), undefined);
  if (a) return a.username || a.displayName || id.toHexString().slice(0, 8);
  const mine = me();
  return mine && mine.isEqual(id) ? 'you' : id.toHexString().slice(0, 8);
}
/** A pilot by username (case-insensitive), or null. */
export function pilotByName(name: string): Identity | null {
  const want = name.trim().toLowerCase();
  if (!want) return null;
  for (const a of safe(() => [...(db()?.account?.iter() ?? [])], [])) if (a.username.toLowerCase() === want) return a.identity;
  return null;
}
export function runs(): HaulRow[] { return safe(() => [...(db()?.portHaul?.iter() ?? [])], []); }
export function run(haulId: bigint): HaulRow | null { return runs().find((h) => h.haulId === haulId) ?? null; }
export function termsOf(haulId: bigint): TermsRow | null { return safe(() => [...(db()?.portHaulTerms?.iter() ?? [])].find((t) => t.haulId === haulId) ?? null, null); }
export function receipts(): ReceiptRow[] { return safe(() => [...(db()?.portReceipt?.iter() ?? [])], []); }
export function statusOf(portId: bigint): StatusRow | null { return safe(() => [...(db()?.portStatus?.iter() ?? [])].find((s) => s.portId === portId) ?? null, null); }
export function ports(): PortRow[] { return safe(() => [...(db()?.planetaryPort?.iter() ?? [])], []); }
export function portOfCity(settlementId: bigint): PortRow | null { return ports().find((p) => p.settlementId === settlementId) ?? null; }
export function crewOf(splitJson: string): ShareIn[] { try { return (JSON.parse(splitJson) as Array<{ k: string; b: number }>).map((c) => ({ key: String(c.k), bps: Number(c.b) })); } catch { return []; } }

/** The yard the port's runs land at (by the run's station key), its wallet and its live ore mark. */
export function yardAt(stationKey: string): { name: string; firmId: bigint; civ: string; cash: bigint | null; mark: bigint | null } {
  const f = safe(() => [...(db()?.spaceFirm?.iter() ?? [])].find((x) => x.kind === 'yard' && x.stationKey === stationKey), undefined);
  const def = !f ? (['aurelian', 'borean'].map((c) => yardDefFor(c)).find((y) => y?.stationKey === stationKey) ?? null) : null;
  const firmId = f?.firmId ?? def?.firmId ?? 0n;
  const name = f?.name ?? def?.name ?? stationKey;
  const w = firmId ? safe(() => db()?.wallet?.identity?.find(new Identity(firmIdentityBits(firmId))), undefined) : undefined;
  let mod = 1;
  for (const m of safe(() => [...(db()?.spacePriceMod?.iter() ?? [])], [])) if (m.stationKey === stationKey && m.commodity === 'ore') mod = m.mult;
  const drift = stationDrift(stationKey, 'ore', marketEpoch(BigInt(Date.now()) * 1000n), sysTierOf(stationKey.split(':')[0] ?? 'sys0'), 'raw');
  const mark = applyPriceMod(marketUnitPrice(5, drift, 'sell'), mod);
  return { name, firmId, civ: f?.civ ?? def?.civ ?? '', cash: w ? w.sov : null, mark };
}
void priceMult; void portFirmIdFor; void SYS0_STATIONS;

/** My active hull and its free hold (the custodian line). */
export function activeHull(): { shipId: bigint; name: string; free: number } | null {
  const mine = me();
  const d = db();
  if (!mine || !d) return null;
  const s = safe(() => [...(d.playerShip?.iter() ?? [])].find((x) => x.owner.isEqual(mine) && x.active), undefined);
  if (!s) return null;
  let used = 0;
  for (const l of safe(() => [...(d.cargoLot?.iter() ?? [])], [])) if (l.locId === `ship:${s.shipId}`) used += l.qty;
  for (const h of runs()) if (h.state === 'aboard' && h.shipId === s.shipId) used += h.qty; // the port's crates are not this client's lots
  const cap = (CHASSIS[s.chassisKey]?.cargo ?? 4) * 25; // the cargo comp's bonus is the server's — a conservative read
  return { shipId: s.shipId, name: s.name, free: Math.max(0, cap - used) };
}

/** Pilots a captain is likely to name: the turret seat's occupant on my hull and my party (never me). */
export function crewCandidates(): Array<{ id: Identity; name: string; why: string }> {
  const mine = me();
  const d = db();
  if (!mine || !d) return [];
  const out = new Map<string, { id: Identity; name: string; why: string }>();
  for (const c of safe(() => [...(d.spaceCrew?.iter() ?? [])], [])) if (c.captain.isEqual(mine) && !c.crew.isEqual(mine)) out.set(c.crew.toHexString(), { id: c.crew, name: nameOf(c.crew), why: 'turret seat' });
  const parties = safe(() => [...(d.partyMember?.iter() ?? [])], []);
  const myParty = parties.find((p) => p.identity.isEqual(mine))?.partyId;
  if (myParty !== undefined) for (const p of parties) if (p.partyId === myParty && !p.identity.isEqual(mine) && !out.has(p.identity.toHexString())) out.set(p.identity.toHexString(), { id: p.identity, name: nameOf(p.identity), why: 'party' });
  return [...out.values()];
}

let names: { seed: string; map: Map<number, string> } | null = null;
/** A settlement's name by its DB id (site.id + 1). */
export function cityName(settlementId: bigint): string {
  const conn = atlas.conn;
  let seed = '';
  try { for (const w of conn?.db.world.iter() ?? []) if (w.kind === 'commons') { seed = w.terrainSeed; break; } } catch { /* */ }
  if (seed && names?.seed !== seed) { try { names = { seed, map: settlementNamesFor(settlementSites(seed), (ss) => civilizationOf(ss)) }; } catch { names = null; } }
  return names?.map.get(Number(settlementId) - 1) ?? `settlement ${settlementId}`;
}
/** The terms window's input for a run (crew as named, by key). */
export function termsInputFor(h: HaulRow, crew: Array<{ key: string; name: string; bps: number }>, partnerName: string): TermsInput {
  const yard = yardAt(h.stationKey);
  const port = ports().find((p) => p.portId === h.portId);
  const hull = activeHull();
  const mine = me();
  return {
    direction: h.direction === 'down' ? 'down' : 'up', haulId: h.haulId, qty: h.qty, familyLabel: h.familyKey === 'ferrite_ore' ? 'ore' : h.familyKey.replace(/_/g, ' '),
    rewardSov: h.rewardSov, portName: port ? (yard.civ === 'borean' ? 'the Borean Planetary Port' : 'the Aurelian Planetary Port') : 'the port',
    cityName: port ? cityName(port.settlementId) : 'the port city', yardName: yard.name, stationKey: h.stationKey,
    hullName: hull?.name ?? '— none: activate a hull in the Hangar first', holdFree: hull?.free ?? 0, yardCash: yard.cash, yardMark: yard.mark,
    captainKey: mine?.toHexString() ?? 'you', captainName: mine ? nameOf(mine) : 'you', crew, partnerName,
  };
}

/** A receipt row as the chain's words need it (names resolved, payouts parsed). */
export function receiptView(r: ReceiptRow, yardOreNow: number | null = null): ReceiptView {
  const terms = termsOf(r.haulId);
  const ms = (t: { microsSinceUnixEpoch: bigint }): number => Number(t.microsSinceUnixEpoch / 1000n);
  let payouts: Array<{ name: string; sov: bigint }> = [];
  try { payouts = (JSON.parse(r.payoutJson) as Array<{ k: string; s: string }>).map((p) => ({ name: nameOf(Identity.fromString(p.k)), sov: BigInt(p.s) })); } catch { payouts = []; }
  return {
    direction: r.direction, stage: r.stage, wait: r.wait, qty: r.qty, yardName: yardAt(r.stationKey).name, civ: r.civ,
    captainName: nameOf(r.captain), delivererName: nameOf(r.deliveredBy), relayFromName: terms ? nameOf(terms.relayFrom) : '—', custodianRev: r.custodianRev,
    termsVersion: r.termsVersion, splitRev: r.splitRev, reward: r.rewardSov, payouts, oreQuote: r.oreQuote, orePaid: r.orePaid,
    yardOreAfter: r.yardOreAfter, yardHulls: r.yardHulls, yardTarget: r.yardTarget, shortHours: r.shortHours, yardOreNow,
    acceptedMs: ms(r.acceptedAt), deliveredMs: ms(r.deliveredAt), builtMs: r.stage === 'delivered' ? 0 : ms(r.builtAt), reserveMs: ms(r.reserveAt),
    reserveHulls: r.reserveHulls, campaignId: r.campaignId, committed: r.committed,
  };
}

/** Receipts this pilot is on (captain, deliverer or paid crew), newest first. */
export function myReceipts(): ReceiptRow[] {
  const mine = me();
  if (!mine) return [];
  const hex = mine.toHexString();
  return receipts().filter((r) => r.captain.isEqual(mine) || r.deliveredBy.isEqual(mine) || r.payoutJson.includes(hex))
    .sort((a, b) => (a.deliveredAt.microsSinceUnixEpoch > b.deliveredAt.microsSinceUnixEpoch ? -1 : 1));
}

/** What this pilot may do about a run right now: nothing, take it over (authorised), or offer it (captain carrying it). */
export function relayRole(h: HaulRow): 'receiver' | 'captain' | null {
  const mine = me();
  const t = termsOf(h.haulId);
  if (!mine || !t || h.state !== 'aboard' || t.custodianRev >= 1) return null;
  if (h.hauler.isEqual(mine) && t.captain.isEqual(mine)) return 'captain';
  const offered = t.offerTo.isEqual(mine) && Number(t.offerExpiresAt.microsSinceUnixEpoch / 1000n) > Date.now();
  if ((!isZero(t.relayPartner) && t.relayPartner.isEqual(mine)) || offered) return 'receiver';
  return null;
}
